/**
 * Created by neo on 02.03.23.
 */
import * as React from 'react';
import { Suspense } from 'react';
import { observer } from 'mobx-react';
import { Theme } from '../../../Theme/Theme';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Translation } from '../../../Components/Translation';
import { ExploreCategorySwitch } from '../../../Components/ExploreCards/ExploreCategorySwitch';
import { useExploreCategoriesSuspense } from '../../../Hooks/useExploreCategoriesSuspense';
import { InitialCategoriesSkeleton } from '../../../Components/Skeletons/InitialCategoriesSkeleton';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  padding-bottom: 48px;
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const CategoriesSection = styled.div`
  background: #fafafa;
  padding: 32px 0 24px;
`;

export type AcademyLibraryScreenProps = {};

// Level-by-level browse: V3 has no flat subtree query, so the "library" now
// shows the Academy root's child categories (each renders its own preview row).
const AcademyLibraryCategories: React.FC = observer(() => {
  const { categories } = useExploreCategoriesSuspense('academy');

  return (
    <>
      {categories.map((category, index) => (
        <ExploreCategorySwitch
          key={category.id}
          category={category}
          categoryIndex={index}
          isLast={index === categories.length - 1}
          linkPrefix="/academy/"
        />
      ))}
    </>
  );
});

export const AcademyLibraryScreen: React.FC<AcademyLibraryScreenProps> = observer(() => {
  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <Translation i18nKey="academyLibrary.title" defaultValue="Academy Library" />
          </HeroTitle>
          <HeroSubtitle>
            <Translation
              i18nKey="academyLibrary.description"
              defaultValue="Qualified international experts lecture on the topics of exercise, mental health and nutrition and provide tips and tricks that are easy to implement in everyday life."
            />
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      <CategoriesSection>
        <Suspense fallback={<InitialCategoriesSkeleton />}>
          <AcademyLibraryCategories />
        </Suspense>
      </CategoriesSection>
    </ScreenContainer>
  );
});
